import { visit, SKIP } from 'unist-util-visit'
import type { Plugin } from 'unified'
import { customElements } from '@/elements'
import { safeJSONParse } from '@/helpers/safe-parse'

/**
 * Plugin to transform child elements into children data attribute of parent element.
 *
 * This plugin processes HTML AST to find parent elements with specified child elements,
 * extracts the data attributes from the children, and adds them as a children array
 * to the parent element's properties.
 *
 * @param options Configuration options
 * @param options.parentTagName The tag name of the parent element
 * @param options.childTagName The tag name of the child elements to process
 * @returns A unified plugin function
 */
export function getNestedElementPlugin(options: {
  parentTagName: string
  childTagName: string
}): Plugin {
  return () => (tree: any) => {
    visit(tree, 'element', (node, _index, _parent) => {
      // Check if this is the specified parent element
      if (
        node.tagName === options.parentTagName &&
        Array.isArray(node.children)
      ) {
        // Find all matching child elements
        const matchingChildren = node.children.filter(
          (child: any) =>
            child.type === 'element' && child.tagName === options.childTagName,
        )

        if (matchingChildren.length > 0) {
          // Extract data attributes from each child
          const childrenData = matchingChildren.map((childNode: any) => {
            // Parse data attributes from the child
            let parsedProperties: { id: string; title: string }

            if (childNode.properties?.dataAttrs) {
              // Case 1: dataAttrs is a string (JSON) that needs to be parsed
              parsedProperties = safeJSONParse(
                childNode.properties.dataAttrs,
              ) as {
                id: string
                title: string
              }
            } else {
              // Case 2: No dataAttrs, use direct properties or fallbacks
              parsedProperties = {
                id:
                  childNode.properties?.id ||
                  childNode.properties?.dataId ||
                  childNode.id,
                title:
                  childNode.properties?.title ||
                  childNode.properties.dataTitle ||
                  childNode.title,
              }
            }

            return parsedProperties
          })

          // Ensure node.properties exists
          node.properties = node.properties || {}

          // Add dataAttrs to parent if it doesn't exist
          if (!node.properties.dataAttrs) {
            node.properties.dataAttrs = '{}'
          }

          // Parse existing dataAttrs
          const dataAttrs = safeJSONParse(node.properties.dataAttrs)

          // Add children array to dataAttrs
          dataAttrs.children = childrenData

          // Update dataAttrs on the node
          node.properties.dataAttrs = JSON.stringify(dataAttrs)

          return SKIP
        }
      }
    })
  }
}

/**
 * Plugin to transform scalar-tab elements into children of scalar-tabs element.
 *
 * This plugin processes HTML AST to find scalar-tabs elements with scalar-tab children,
 * extracts the data attributes from the child tabs, and adds them as a children array
 * to the parent scalar-tabs element's properties.
 *
 */
export function nestedScalarTabs(): Plugin {
  return getNestedElementPlugin({
    parentTagName: customElements['scalar-tabs'].tagName,
    childTagName: customElements['scalar-tab'].tagName,
  })
}
